import type { ReactNode } from 'react';

import { useTranslation } from '@hooks/useTranslation';
import Modal from '@components/ui/Modal/Modal';
import Button from '@components/ui/Button/Button';
import AlertSuccessIcon from '@assets/icons/AlertSuccessIcon';
import AlertDangerIcon from '@assets/icons/AlertDangerIcon';
import AlertWarningIcon from '@assets/icons/AlertWarningIcon';
import AlertErrorIcon from '@assets/icons/AlertErrorIcon';
import styles from './AlertModal.module.scss';

// ============================================
// TYPES
// ============================================

export type AlertVariant = 'success' | 'danger' | 'warning' | 'error';
export type AlertModalSize = 'sm' | 'md';

export interface AlertModalProps {
  /** Alert type — controls icon, color scheme, and ARIA semantics */
  variant: AlertVariant;
  /** Alert title displayed next to the icon */
  title: string;
  /** Descriptive body text explaining the alert context */
  description: ReactNode;
  /** Whether the alert modal is visible */
  isOpen: boolean;
  /** Callback when the modal should close */
  onClose: () => void;
  /** Label for the primary action button (e.g., "Delete", "Try again", "Continue") */
  primaryLabel: string;
  /** Callback when the primary action button is clicked */
  onPrimaryClick: () => void;
  /** Label for the secondary action button */
  secondaryLabel?: string;
  /** Callback when the secondary button is clicked (defaults to onClose) */
  onSecondaryClick?: () => void;
  /** Modal size — sm (480px) or md (640px) */
  size?: AlertModalSize;
  /** Show loading spinner on the primary button and disable interaction */
  loading?: boolean;
  /** Custom icon to override the default variant icon */
  icon?: ReactNode;
}

// ============================================
// VARIANT CONFIG
// ============================================

const VARIANT_ICONS: Record<AlertVariant, () => ReactNode> = {
  success: () => <AlertSuccessIcon />,
  danger: () => <AlertDangerIcon />,
  warning: () => <AlertWarningIcon />,
  error: () => <AlertErrorIcon />,
};

const VARIANT_BUTTON_MAP: Record<AlertVariant, 'filled' | 'danger'> = {
  success: 'filled',
  danger: 'danger',
  warning: 'filled',
  error: 'danger',
};

// ============================================
// COMPONENT
// ============================================

/**
 * AlertModal — A generic alert dialog for confirmations, errors, warnings, and success messages.
 *
 * Composes the existing Modal component and adds variant-specific icon, color scheme,
 * and pre-wired footer buttons. Supports success, danger, warning, and error variants.
 *
 * @example
 * ```tsx
 * // Delete confirmation
 * <AlertModal
 *   variant="danger"
 *   title="Delete Item"
 *   description="This action cannot be undone. Are you sure?"
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   primaryLabel="Delete"
 *   onPrimaryClick={handleDelete}
 * />
 *
 * // Success notification
 * <AlertModal
 *   variant="success"
 *   title="Saved Successfully"
 *   description="Your changes have been saved."
 *   isOpen={isOpen}
 *   onClose={handleClose}
 *   primaryLabel="Continue"
 *   onPrimaryClick={handleClose}
 *   size="sm"
 * />
 * ```
 */
function AlertModal({
  variant,
  title,
  description,
  isOpen,
  onClose,
  primaryLabel,
  onPrimaryClick,
  secondaryLabel: secondaryLabelProp,
  onSecondaryClick,
  size = 'sm',
  loading = false,
  icon,
}: AlertModalProps) {
  const { t } = useTranslation();
  const secondaryLabel = secondaryLabelProp ?? t('K_CANCEL', 'Cancel');
  const variantIcon = icon ?? VARIANT_ICONS[variant]();
  const primaryButtonVariant = VARIANT_BUTTON_MAP[variant];
  const handleSecondaryClick = onSecondaryClick ?? onClose;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title=""
      size={size}
      hideCloseButton
    >
      <div className={styles.content} role="alertdialog" aria-labelledby="alert-modal-title" aria-describedby="alert-modal-description">
        <div className={styles.topRow}>
          <div className={`${styles.iconWrapper} ${styles[variant]}`}>
            {variantIcon}
          </div>
          <button
            type="button"
            className={styles.closeButton}
            onClick={onClose}
            aria-label="Close alert"
          >
            <svg viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M15 5L5 15M5 5l10 10"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className={styles.body}>
          <h3 id="alert-modal-title" className={styles.title}>
            {title}
          </h3>
          <div id="alert-modal-description" className={styles.description}>
            {description}
          </div>
        </div>

        <div className={styles.actions}>
          <Button
            variant="outline"
            size="md"
            onClick={handleSecondaryClick}
            disabled={loading}
          >
            {secondaryLabel}
          </Button>
          <Button
            variant={primaryButtonVariant}
            size="md"
            onClick={onPrimaryClick}
            loading={loading}
          >
            {primaryLabel}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export default AlertModal;
